import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link as LinkS } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../utils/cn";
import Magnetic from "./ui/Magnetic";
import { useTheme } from "../contexts/ThemeContext";
import LanguageSwitcher from "./ui/LanguageSwitcher";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { toggleTheme, isDark } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenuClick = () => {
    setIsOpen(!isOpen);
    // Prevent body scroll when menu is open
    if (!isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = "unset";
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = "unset";
  }, [location.pathname]);

  const navItems = [
    { number: "01", label: t('nav.about'), to: "about" },
    { number: "02", label: t('nav.experience'), to: "experience" },
    { number: "03", label: t('nav.work'), to: "work" },
    { number: "04", label: t('nav.contact'), to: "contact" },
  ];


  // Component to render appropriate link based on current page
  const NavLink = ({ item, className, onClick, children }) => {
    const baseClassName = className || "";

    if (isHomePage) {
      // Use react-scroll for scrolling, let scroll spy handle URL updates
      return (
        <LinkS
          to={item.to}
          spy={true}
          smooth={true}
          offset={-120}
          duration={500}
          className={baseClassName}
          onClick={onClick}
        >
          {children}
        </LinkS>
      );
    } else {
      // Use router link to navigate to home page with hash
      return (
        <RouterLink
          to={`/#${item.to}`}
          className={baseClassName}
          onClick={onClick}
        >
          {children}
        </RouterLink>
      );
    }
  };

  // Smart logo click handler
  const handleLogoClick = (e) => {
    if (isHomePage) {
      // If on home page, scroll to top
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    // If on other pages, let RouterLink handle navigation to home
  };

  return (
    <motion.header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled ? "bg-navy/90 backdrop-blur-md shadow-big" : "bg-transparent"
      )}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1 }}
    >
      <div className="max-w-full mx-auto px-6 sm:px-8 lg:px-12">
        <nav className="flex items-center justify-between h-20 lg:h-24 relative z-[130]">
          {/* Logo */}
          <Magnetic strength={0.3}>
            <motion.div
              className="flex-shrink-0 z-[140]"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <RouterLink
                to="/"
                onClick={handleLogoClick}
                className="flex items-center justify-center w-12 h-12 border-2 border-accent text-accent rounded hover:bg-accent/10 transition-all duration-300 interactive relative z-[140]"
              >
                <span className="font-mono text-base font-bold tracking-tight">
                  AWN
                </span>
              </RouterLink>
            </motion.div>
          </Magnetic>

          {/* Navigation - Desktop and Mobile */}
          <div className="flex items-center relative z-[130]">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              <ol className="flex items-center space-x-8">
                {navItems.map((item) => (
                  <li key={item.to}>
                    <Magnetic strength={0.15}>
                      <NavLink
                        item={item}
                        className="group flex items-center py-3 text-sm font-mono text-slate hover:text-accent transition-colors duration-200 cursor-pointer interactive"
                      >
                        <span className="text-accent mr-2 group-hover:mr-3 transition-all duration-200">
                          {item.number}.
                        </span>
                        {item.label}
                      </NavLink>
                    </Magnetic>
                  </li>
                ))}
              </ol>

              <div className="flex items-center space-x-4 ml-8">
                <LanguageSwitcher />

                <Magnetic strength={0.2}>
                  <motion.button
                    onClick={toggleTheme}
                    className="p-3 border border-accent text-accent rounded-full hover:bg-accent/10 transition-all duration-200 interactive"
                    whileHover={{ y: -2, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Toggle theme"
                  >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                  </motion.button>
                </Magnetic>
              </div>
            </div>

            {/* Mobile menu button - FIXED Z-INDEX */}
            <Magnetic strength={0.3}>
              <button
                className="lg:hidden p-3 text-accent hover:bg-accent/10 transition-colors duration-200 rounded-md interactive relative z-[150]"
                onClick={handleMenuClick}
                aria-label="Toggle menu"
                style={{ zIndex: 150 }} // Force z-index with inline style
              >
                <div className="w-6 h-6 relative">
                  {!isOpen ? (
                    // Hamburger Icon
                    <div className="flex flex-col justify-center h-full space-y-1">
                      <span className="block w-6 h-0.5 bg-accent"></span>
                      <span className="block w-6 h-0.5 bg-accent"></span>
                      <span className="block w-6 h-0.5 bg-accent"></span>
                    </div>
                  ) : (
                    // X Icon
                    <div className="flex items-center justify-center h-full">
                      <div className="relative w-6 h-6">
                        <span className="block w-6 h-0.5 bg-accent absolute top-1/2 left-0 transform -translate-y-1/2 rotate-45"></span>
                        <span className="block w-6 h-0.5 bg-accent absolute top-1/2 left-0 transform -translate-y-1/2 -rotate-45"></span>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            </Magnetic>
          </div>
        </nav>

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop - UPDATED Z-INDEX */}
              <motion.div
                className="lg:hidden fixed inset-0 bg-navy/70 backdrop-blur-lg z-[90]"
                initial={{
                  opacity: 0,
                  backdropFilter: "blur(0px)",
                  WebkitBackdropFilter: "blur(0px)",
                }}
                animate={{
                  opacity: 1,
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
                exit={{
                  opacity: 0,
                  backdropFilter: "blur(0px)",
                  WebkitBackdropFilter: "blur(0px)",
                }}
                transition={{ duration: 0.4 }}
                onClick={closeMenu}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 90,
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  backgroundColor: "rgba(10, 25, 47, 0.7)",
                }}
              />

              {/* Sliding Menu Panel - UPDATED Z-INDEX */}
              <motion.div
                className="lg:hidden fixed top-0 right-0 w-full max-w-sm h-full bg-navy shadow-2xl z-[95] border-l border-accent/20"
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  duration: 0.4,
                }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  height: "100vh",
                  zIndex: 95,
                }}
              >
                {/* Menu Content */}
                <div className="flex flex-col justify-center h-full px-8 py-20">
                  <nav className="space-y-2">
                    {navItems.map((item, index) => (
                      <motion.div
                        key={item.to}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: index * 0.1 + 0.2,
                          ease: "easeOut",
                        }}
                      >
                        <NavLink
                          item={item}
                          onClick={closeMenu}
                          className="group flex flex-col py-4 px-4 rounded-lg hover:bg-accent/5 transition-all duration-300 cursor-pointer w-full text-left"
                        >
                          <span className="text-accent text-xs font-mono mb-1 group-hover:text-secondary transition-colors duration-200">
                            {item.number}.
                          </span>
                          <span className="text-lighterSlate text-xl font-medium group-hover:text-accent group-hover:translate-x-2 transition-all duration-300">
                            {item.label}
                          </span>
                        </NavLink>
                      </motion.div>
                    ))}
                  </nav>

                  {/* Action Buttons */}
                  <motion.div
                    className="mt-16 space-y-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <LanguageSwitcher />

                      <motion.button
                        onClick={() => {
                          toggleTheme();
                          closeMenu();
                        }}
                        className="flex items-center justify-center w-12 h-12 border border-accent/30 text-accent rounded-full hover:bg-accent/10 hover:border-accent transition-all duration-200"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Toggle theme"
                      >
                        {isDark ? <Sun size={18} /> : <Moon size={18} />}
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
